/**
 * What the autopilot is allowed to do: five numbers and two switches, in the
 * panel the Skannid tab's engine strip opens from its gear.
 *
 * Numbers are edited locally and committed on blur, so typing "2" on the way to
 * "20" does not immediately become the new batch size and send a save for a
 * value nobody meant (`prospecting/engines/EngineNumberField.tsx`, shared with
 * the brain autopilot's form). The switches write straight through - there is
 * nothing to mistype in a boolean, and a switch with a save step is not a
 * switch.
 */
import { useId } from "react";
import { Label, Switch } from "@wa/ui";
import type { AutopilotSettings as Settings } from "../../../../lib/scanLeads";
import { EngineNumberField } from "../../prospecting/engines";
import type { T } from "../../site-scanner/types";

function SwitchRow({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex items-start gap-3">
      <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onChange} className="mt-0.5" />
      <span className="min-w-0">
        <Label htmlFor={id} className="block text-[13px] font-[550] leading-snug text-foreground">
          {label}
        </Label>
        <span className="crm-sub block text-[11.5px] leading-snug">{hint}</span>
      </span>
    </div>
  );
}

export function AutopilotSettings({
  settings,
  canManage,
  saving,
  onPatch,
  t,
}: {
  settings: Settings;
  canManage: boolean;
  saving: boolean;
  onPatch: (change: Partial<Settings>) => void;
  t: T;
}) {
  const s = settings;
  const locked = !canManage || saving;

  return (
    // Three columns at most: the form lives in a side panel now, not across
    // the full width of the page, and five numbers in a row there leave each
    // hint two words wide.
    <div className="mt-5 space-y-5 border-t border-[var(--crm-hair)] px-[26px] pb-6 pt-5">
      <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
        <EngineNumberField
          label={t("leads.autopilot.field.interval")}
          hint={t("leads.autopilot.hint.interval")}
          value={s.intervalMinutes}
          min={15}
          max={1440}
          disabled={locked}
          onCommit={(v) => onPatch({ intervalMinutes: v })}
        />
        <EngineNumberField
          label={t("leads.autopilot.field.batch")}
          hint={t("leads.autopilot.hint.batch")}
          value={s.batchSize}
          min={1}
          max={100}
          disabled={locked}
          onCommit={(v) => onPatch({ batchSize: v })}
        />
        <EngineNumberField
          label={t("leads.autopilot.field.rescan")}
          hint={t("leads.autopilot.hint.rescan")}
          value={s.rescanAfterDays}
          min={1}
          max={365}
          disabled={locked}
          onCommit={(v) => onPatch({ rescanAfterDays: v })}
        />
        <EngineNumberField
          label={t("leads.autopilot.field.minScore")}
          hint={t("leads.autopilot.hint.minScore")}
          value={s.minScore}
          min={0}
          max={100}
          disabled={locked}
          onCommit={(v) => onPatch({ minScore: v })}
        />
        <EngineNumberField
          label={t("leads.autopilot.field.dailyCap")}
          hint={t("leads.autopilot.hint.dailyCap")}
          value={s.dailyCap}
          min={10}
          max={5000}
          disabled={locked}
          onCommit={(v) => onPatch({ dailyCap: v })}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <SwitchRow
          label={t("leads.autopilot.field.requireContact")}
          hint={t("leads.autopilot.hint.requireContact")}
          checked={s.requireContact}
          disabled={locked}
          onChange={(v) => onPatch({ requireContact: v })}
        />
        <SwitchRow
          label={t("leads.autopilot.field.discover")}
          hint={t("leads.autopilot.hint.discover")}
          checked={s.discoverFromRegister}
          disabled={locked}
          onChange={(v) => onPatch({ discoverFromRegister: v })}
        />
      </div>
    </div>
  );
}
